import {memo, ReactNode} from 'react';
import propsAreEqual from 'react-fast-compare';
import Spacer from 'patient-ping-remedy/packages/spacer';
import {StandardSemiBoldTypography} from 'patient-ping-remedy/packages/typography';
import {NoElementsWrapper, MarkAllAsRead, ElementGroup, TimeCategoryHeader, TimeCategoryHeaderWrapper}
  from '@common/widgets/WidgetElementList.styles';
import Helpers from '@/helpers/helpers';

type Props<T> = {
  elements: T[];
  totalUnreadElements: number;
  dateProperty: string;
  title: string;
  isApiLoading: boolean;
  markAllAsRead(): void;
  render(element: T): ReactNode;
}

function WidgetElementList<T>({
  elements,
  totalUnreadElements,
  dateProperty,
  title,
  isApiLoading,
  markAllAsRead,
  render
}: Props<T>) {
  const categorizedElements = Helpers.getCategorizedElements(elements, dateProperty);

  const todayElements = categorizedElements.todayElements;
  const yesterdayElements = categorizedElements.yesterdayElements;
  const olderElements = categorizedElements.olderElements;

  if (elements.length === 0) {
    return (
      <NoElementsWrapper>
        <StandardSemiBoldTypography>All caught up! No unread {title.toLowerCase()}.</StandardSemiBoldTypography>
      </NoElementsWrapper>
    );
  }

  const markAllAsReadElement = (
    <MarkAllAsRead
      disabled={isApiLoading || totalUnreadElements === 0}
      onClick={() => markAllAsRead()}
      data-testid="mark-all-as-read"
    >
      Mark all as read
    </MarkAllAsRead>
  );

  return (
    <>
      {todayElements.length > 0 && (
        <ElementGroup>
          <TimeCategoryHeaderWrapper>
            <TimeCategoryHeader>Today</TimeCategoryHeader>
            {markAllAsReadElement}
          </TimeCategoryHeaderWrapper>
          {todayElements.map(render)}
        </ElementGroup>
      )}

      {yesterdayElements.length > 0 && (
        <ElementGroup>
          {todayElements.length > 0 && <Spacer itemHeight="30px" />}
          <TimeCategoryHeaderWrapper>
            <TimeCategoryHeader>Yesterday</TimeCategoryHeader>
            {todayElements.length === 0 && markAllAsReadElement}
          </TimeCategoryHeaderWrapper>
          {yesterdayElements.map(render)}
        </ElementGroup>
      )}

      {olderElements.length > 0 && (
        <ElementGroup>
          {(todayElements.length > 0 || yesterdayElements.length > 0) && <Spacer itemHeight="30px" />}
          <TimeCategoryHeaderWrapper>
            <TimeCategoryHeader>Older</TimeCategoryHeader>
            {todayElements.length === 0 && yesterdayElements.length === 0 && markAllAsReadElement}
          </TimeCategoryHeaderWrapper>
          {olderElements.map(render)}
        </ElementGroup>
      )}
    </>
  );
}

export default memo(WidgetElementList, propsAreEqual);
